import {
  Modal,
  Button,
  Form,
  Card,
  Tabs,
  Tab,
  Spinner,
  Badge,
  Table,
} from "react-bootstrap";
import { useState, useEffect } from "react";
import { format } from "date-fns";
import moment from "moment";
import currency from "currency.js";
import {
  FaTruck,
  FaGasPump,
  FaCalendar,
  FaMapMarkerAlt,
  FaTools,
  FaFileAlt,
  FaRoute,
  FaDollarSign,
  FaShieldAlt,
  FaEdit,
  FaDownload,
  FaEye,
  FaCalendarAlt,
  FaClock,
} from "react-icons/fa";
import TableComponent from "../TableComponent";
import { getDaysUntilExpiry } from "../../utils/helpers";
import DocumentUploadModal from "./DocumentUploadModal";
import DocumentsTab from "./DocumentsTab";

function VehicleDetailsModal({
  show,
  onHide,
  vehicle,
  refetch,
  backendUrl,
  token,
}) {
  const [activeTab, setActiveTab] = useState("overview");
  const [loadingData, setLoadingData] = useState(false);
  const [showModal, setShowModal] = useState("");

  useEffect(() => {
    console.log(vehicle);
  }, [vehicle]);

  if (!vehicle) return null;
  // Helper function to format currency
  const formatCurrency = (value) => {
    return currency(value || 0, { symbol: "₦", precision: 0 }).format();
  };

  const driverFullname = (driver) => {
    const {employee} = driver
    if (employee) {
      let { employee } = driver;
      return `${employee.Title} ${employee.Name}`;
    }
    return driver.employee_id;
  };

  // const handleActionButton = (id) => {
  //     setShowModal(activeTab)

  // }

  // Helper function to get status badge
  const getStatusBadge = (status) => {
    const statusMap = {
      available: "success",
      in_use: "primary",
      maintenance: "warning",
      out_of_service: "danger",
      retired: "secondary",
    };
    return statusMap[status] || "secondary";
  };

  // Helper function to get trip status badge
  const getTripStatusBadge = (status) => {
    const statusMap = {
      planned: "info",
      in_progress: "primary",
      completed: "success",
      cancelled: "danger",
      delayed: "warning",
    };
    return statusMap[status] || "secondary";
  };

  // Overview Tab Content
  const renderOverviewTab = () => (
    <div>
      {/* Vehicle Header Card */}
      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <div className="d-flex gap-3 align-items-center">
            <FaTruck size={24} className="text-primary" />
            <div>
              <h5 className="mb-0">
                {vehicle.make} {vehicle.model}
              </h5>
              <small className="text-muted">{vehicle.fleetNumber}</small>
            </div>
          </div>
          <div className="d-flex gap-2">
            <Badge bg={getStatusBadge(vehicle.status)}>
              {vehicle.status?.replace("_", " ").toUpperCase()}
            </Badge>
            <Badge bg={vehicle.isActive ? "success" : "secondary"}>
              {vehicle.isActive ? "Active" : "Inactive"}
            </Badge>
          </div>
        </Card.Header>
        <Card.Body>
          <div className="row">
            <div className="col-md-6">
              <h6 className="text-muted mb-3">Basic Information</h6>
              <p>
                <strong>License Plate:</strong> {vehicle.licensePlate}
              </p>
              <p>
                <strong>VIN:</strong> {vehicle.vin}
              </p>
              <p>
                <strong>Year:</strong> {vehicle.year}
              </p>
              <p>
                <strong>Color:</strong> {vehicle.color}
              </p>
              <p>
                <strong>Warehouse:</strong> {vehicle.warehouse}
              </p>
            </div>
            <div className="col-md-6">
              <h6 className="text-muted mb-3">Specifications</h6>
              <p>
                <FaGasPump className="text-primary me-2" />
                <strong>Fuel Type:</strong> {vehicle.fuelType}
              </p>
              <p>
                <strong>Fuel Capacity:</strong> {vehicle.fuelCapacity} L
              </p>
              <p>
                <strong>Weight Capacity:</strong> {vehicle.capacityWeight} kg
              </p>
              <p>
                <strong>Volume Capacity:</strong> {vehicle.capacityVolume} m³
              </p>
              <p>
                <strong>GPS Device:</strong> {vehicle.gpsDeviceId || "N/A"}
              </p>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Usage Statistics */}
      <div className="row mb-4">
        <div className="col-md-3">
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-primary">
                {vehicle.odometer?.toLocaleString()}
              </h3>
              <small className="text-muted">Odometer (miles)</small>
            </Card.Body>
          </Card>
        </div>
        <div className="col-md-3">
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-primary">{vehicle.engineHours}</h3>
              <small className="text-muted">Engine Hours</small>
            </Card.Body>
          </Card>
        </div>
        <div className="col-md-3">
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-primary">{vehicle.trips?.length || 0}</h3>
              <small className="text-muted">Total Trips</small>
            </Card.Body>
          </Card>
        </div>
        <div className="col-md-3">
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-primary">{vehicle.expenses?.length || 0}</h3>
              <small className="text-muted">Expenses</small>
            </Card.Body>
          </Card>
        </div>
      </div>

      {/* Purchase Information */}
      <Card className="mb-4">
        <Card.Header>
          <FaDollarSign className="me-2" />
          Purchase Information
        </Card.Header>
        <Card.Body>
          <div className="row">
            <div className="col-md-6">
              <p>
                <FaCalendar className="text-primary me-2" />
                <strong>Purchase Date:</strong>{" "}
                {vehicle.purchaseDate
                  ? moment(vehicle.purchaseDate).format("MMM DD, YYYY")
                  : "N/A"}
              </p>
            </div>
            <div className="col-md-6">
              <p>
                <strong>Purchase Price:</strong>{" "}
                {formatCurrency(vehicle.purchasePrice)}
              </p>
            </div>
          </div>
          {vehicle.notes && (
            <div className="mt-3">
              <strong>Notes:</strong>
              <p className="text-muted mb-0">{vehicle.notes}</p>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Recent Activity */}
      <Card>
        <Card.Header>Recent Activity</Card.Header>
        <Card.Body>
          <p>
            <strong>Created:</strong>{" "}
            {moment(vehicle.createdAt).format("MMM DD, YYYY HH:mm")}
          </p>
          <p className="mb-0">
            <strong>Last Updated:</strong>{" "}
            {moment(vehicle.updatedAt).format("MMM DD, YYYY HH:mm")}
          </p>
        </Card.Body>
      </Card>
    </div>
  );

  // Trips Tab Content
  const renderTripsTab = () => {
    const trips = vehicle.trips || [];

    const tripsTableHead = () => (
      <thead>
        <tr>
          <th>Trip Number</th>
          <th>Driver</th>
          <th>Route</th>
          <th>Start Time</th>
          <th>End Time</th>
          <th>Distance</th>
          <th>Status</th>
          <th>Purpose</th>
        </tr>
      </thead>
    );

    const tripsDisplayTable = (trip) => (
      <>
        <td>{trip.tripNumber}</td>
        <td>{driverFullname(trip.driver)}</td>
        <td>{trip.route.name || trip.route_id || "Custom"}</td>
        <td>{moment(trip.plannedStartTime).format("MMM DD, HH:mm")}</td>
        <td>
          {trip.plannedEndTime
            ? moment(trip.plannedEndTime).format("MMM DD, HH:mm")
            : "N/A"}
        </td>
        <td>{trip.plannedDistance || trip.actualDistance || "N/A"} miles</td>
        <td>
          <Badge bg={getTripStatusBadge(trip.status)}>
            {trip.status?.replace("_", " ").toUpperCase()}
          </Badge>
        </td>
        <td>{trip.purpose}</td>
      </>
    );

    return (
      <div>
        {/* <Card className="mb-3">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <div>
              <FaRoute className="me-2" />
              Trip History
            </div>
            <Badge bg="primary">{trips.length} Total</Badge>
          </Card.Header>
        </Card> */}

        {trips.length > 0 ? (
          <TableComponent
            responsive
            borderless
            striped
            tableHeadsFunction={tripsTableHead}
            mainDataArray={trips}
            tableDataRowFunction={tripsDisplayTable}
            className="product-table text-nowrap"
          />
        ) : (
          <Card>
            <Card.Body className="text-center text-muted py-5">
              <FaRoute size={48} className="mb-3 opacity-50" />
              <p>No trips recorded for this vehicle</p>
            </Card.Body>
          </Card>
        )}
      </div>
    );
  };

  // Expenses Tab Content
  const renderExpensesTab = () => {
    const expenses = vehicle.expenses?.filter((e) => e.expense_id) || [];
    const totalExpenses = expenses.reduce(
      (sum, exp) => sum + (parseFloat(exp.amount) || 0),
      0
    );

    const expensesTableHead = () => (
      <thead>
        <tr>
          <th>Date</th>
          <th>Category</th>
          <th>Subcategory</th>
          <th>Description</th>
          <th>Amount</th>
          <th>Trip</th>
        </tr>
      </thead>
    );

    const expensesDisplayTable = (expense) => (
      <>
        <td>{moment(expense.expenseDate).format("MMM DD, YYYY")}</td>
        <td>{expense.category}</td>
        <td>{expense.subcategory || "N/A"}</td>
        <td>{expense.description}</td>
        <td className="text-end">{formatCurrency(expense.amount)}</td>
        <td>{expense.trip_id || "N/A"}</td>
      </>
    );

    return (
      <div>
        {/* <Card className="mb-3">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <div>
              <FaDollarSign className="me-2" />
              Expense History
            </div>
            <div>
              <strong>Total: {formatCurrency(totalExpenses)}</strong>
            </div>
          </Card.Header>
        </Card> */}

        {expenses.length > 0 ? (
          <TableComponent
            responsive
            borderless
            striped
            tableHeadsFunction={expensesTableHead}
            mainDataArray={expenses}
            tableDataRowFunction={expensesDisplayTable}
            className="product-table text-nowrap"
          />
        ) : (
          <Card>
            <Card.Body className="text-center text-muted py-5">
              <FaDollarSign size={48} className="mb-3 opacity-50" />
              <p>No expenses recorded for this vehicle</p>
            </Card.Body>
          </Card>
        )}
      </div>
    );
  };

  // Components Tab Content
  const renderComponentsTab = () => {
    const components = vehicle.components?.filter((c) => c.component_id) || [];

    const componentsTableHead = () => (
      <thead>
        <tr>
          <th>Name</th>
          <th>CIN</th>
          <th>Make/Model</th>
          <th>Year</th>
          <th>Serial Number</th>
          <th>Category</th>
          <th>Status</th>
          <th>Notes</th>
        </tr>
      </thead>
    );

    const componentsDisplayTable = (component) => {
      const getComponentStatus = (status) => {
        const statusMap = {
          available: "success",
          installed: "primary",
          maintenance: "warning",
          damaged: "danger",
        };
        return statusMap[status] || "secondary";
      };

      return (
        <>
          <td>{component.name}</td>
          <td>{component.cin || "N/A"}</td>
          <td>
            {component.make} {component.model}
          </td>
          <td>{component.year || "N/A"}</td>
          <td>{component.serial_number || "N/A"}</td>
          <td>{component.category}</td>
          <td>
            <Badge bg={getComponentStatus(component.status)}>
              {component.status?.toUpperCase()}
            </Badge>
          </td>
          <td>{component.notes || "-"}</td>
        </>
      );
    };

    return (
      <div>
        {/* <Card className="mb-3">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <div>
              <FaTools className="me-2" />
              Vehicle Components
            </div>
            <Badge bg="primary">{components.length} Total</Badge>
          </Card.Header>
        </Card> */}

        {components.length > 0 ? (
          <TableComponent
            responsive
            borderless
            striped
            tableHeadsFunction={componentsTableHead}
            mainDataArray={components}
            tableDataRowFunction={componentsDisplayTable}
            className="product-table text-nowrap"
          />
        ) : (
          <Card>
            <Card.Body className="text-center text-muted py-5">
              <FaTools size={48} className="mb-3 opacity-50" />
              <p>No components registered for this vehicle</p>
            </Card.Body>
          </Card>
        )}
      </div>
    );
  };

  // Insurance Tab Content
  const renderInsuranceTab = () => {
    const insurances = vehicle.insurances?.filter((i) => i.insurance_id) || [];

    const insuranceTableHead = () => (
      <thead>
        <tr>
          <th>Provider</th>
          <th>Policy Number</th>
          <th>Registration Date</th>
          <th>Insurance Expiry</th>
          <th>Inspection Expiry</th>
          <th>Status</th>
          <th>Notes</th>
        </tr>
      </thead>
    );

    const insuranceDisplayTable = (insurance) => {
      const isExpired =
        insurance.insuranceExpiry &&
        moment(insurance.insuranceExpiry).isBefore(moment());

      return (
        <>
          <td>{insurance.insuranceProvider}</td>
          <td>{insurance.insurancePolicyNumber}</td>
          <td>
            {insurance.registrationDate
              ? moment(insurance.registrationDate).format("MMM DD, YYYY")
              : "N/A"}
          </td>
          <td className={isExpired ? "text-danger" : ""}>
            {insurance.insuranceExpiry
              ? moment(insurance.insuranceExpiry).format("MMM DD, YYYY")
              : "N/A"}
            {isExpired && (
              <Badge bg="danger" className="ms-2">
                Expired
              </Badge>
            )}
          </td>
          <td>
            {insurance.inspectionExpiry
              ? moment(insurance.inspectionExpiry).format("MMM DD, YYYY")
              : "N/A"}
          </td>
          <td>
            <Badge bg={insurance.status === "active" ? "success" : "secondary"}>
              {insurance.status?.toUpperCase()}
            </Badge>
          </td>
          <td>{insurance.notes || "-"}</td>
        </>
      );
    };

    return (
      <div>
        {/* <Card className="mb-3">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <div>
              <FaShieldAlt className="me-2" />
              Insurance Records
            </div>
            <Badge bg="primary">{insurances.length} Total</Badge>
          </Card.Header>
        </Card> */}

        {insurances.length > 0 ? (
          <TableComponent
            responsive
            borderless
            striped
            tableHeadsFunction={insuranceTableHead}
            mainDataArray={insurances}
            tableDataRowFunction={insuranceDisplayTable}
            className="product-table text-nowrap"
          />
        ) : (
          <Card>
            <Card.Body className="text-center text-muted py-5">
              <FaShieldAlt size={48} className="mb-3 opacity-50" />
              <p>No insurance records for this vehicle</p>
            </Card.Body>
          </Card>
        )}
      </div>
    );
  };

  // Documents Tab Content
  const renderDocumentsTab = () => {
    const documents = vehicle.documents?.filter((d) => d.document_id) || [];

    <DocumentsTab documents={documents}/>
  };

  // GPS Tracking Tab Content
  const renderTrackingTab = () => {
    const trackings = vehicle.trackings || [];

    const trackingTableHead = () => (
      <thead>
        <tr>
          <th>Timestamp</th>
          <th>Location</th>
          <th>Speed</th>
          <th>Heading</th>
          <th>Odometer</th>
          <th>Engine Status</th>
          <th>Fuel Level</th>
          <th>Engine Temp</th>
        </tr>
      </thead>
    );

    const trackingDisplayTable = (tracking) => (
      <>
        <td>{moment(tracking.timestamp).format("MMM DD, YYYY HH:mm")}</td>
        <td>
          {tracking.latitude && tracking.longitude ? (
            <a
              href={`https://www.google.com/maps?q=${tracking.latitude},${
                tracking.longitude
              }`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {tracking.address ||
                `${tracking.latitude}, ${tracking.longitude}`}
            </a>
          ) : (
            "N/A"
          )}
        </td>
        <td>{tracking.speed ? `${tracking.speed} km/h` : "N/A"}</td>
        <td>{tracking.heading ? `${tracking.heading}°` : "N/A"}</td>
        <td>
          {tracking.odometer ? tracking.odometer.toLocaleString() : "N/A"}
        </td>
        <td>
          {tracking.engineStatus && (
            <Badge
              bg={tracking.engineStatus === "running" ? "success" : "secondary"}
            >
              {tracking.engineStatus}
            </Badge>
          )}
        </td>
        <td>{tracking.fuelLevel ? `${tracking.fuelLevel}%` : "N/A"}</td>
        <td>{tracking.engineTemp ? `${tracking.engineTemp}°C` : "N/A"}</td>
      </>
    );

    return (
      <div>
        {/* <Card className="mb-3">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <div>
              <FaMapMarkerAlt className="me-2" />
              GPS Tracking History
            </div>
            <Badge bg="primary">{trackings.length} Records</Badge>
          </Card.Header>
        </Card> */}

        {trackings.length > 0 ? (
          <TableComponent
            responsive
            borderless
            striped
            tableHeadsFunction={trackingTableHead}
            mainDataArray={trackings}
            tableDataRowFunction={trackingDisplayTable}
            className="product-table text-nowrap"
          />
        ) : (
          <Card>
            <Card.Body className="text-center text-muted py-5">
              <FaMapMarkerAlt size={48} className="mb-3 opacity-50" />
              <p>No GPS tracking data available</p>
            </Card.Body>
          </Card>
        )}
      </div>
    );
  };

  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        backdropClassName="global-backdrop"
        centered
        dialogClassName="my-style-modal"
        animation={false}
        enforceFocus={false}
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <div>
              <h4>
                {vehicle.make} {vehicle.model} - {vehicle.fleetNumber}
              </h4>
              <small className="text-muted">{vehicle.licensePlate}</small>
            </div>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="container-fluid">
            <Tabs
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k || "overview")}
              className="mb-3"
            >
              <Tab eventKey="overview" title="Overview">
                {renderOverviewTab()}
              </Tab>

              <Tab
                eventKey="trips"
                title={`Trips (${vehicle.trips?.length || 0})`}
              >
                {renderTripsTab()}
              </Tab>

              <Tab
                eventKey="expenses"
                title={`Expenses (${vehicle.expenses?.filter(
                  (e) => e.expense_id
                )?.length || 0})`}
              >
                {renderExpensesTab()}
              </Tab>

              <Tab
                eventKey="components"
                title={`Components (${vehicle.components?.filter(
                  (c) => c.component_id
                )?.length || 0})`}
              >
                {renderComponentsTab()}
              </Tab>

              <Tab
                eventKey="insurance"
                title={`Insurance (${vehicle.insurances?.filter(
                  (i) => i.insurance_id
                )?.length || 0})`}
              >
                {renderInsuranceTab()}
              </Tab>

              <Tab
                eventKey="documents"
                title={`Documents (${vehicle.documents?.filter(
                  (d) => d.document_id
                )?.length || 0})`}
              >
                {renderDocumentsTab()}
              </Tab>

              <Tab
                eventKey="tracking"
                title={`GPS (${vehicle.trackings?.length || 0})`}
              >
                {renderTrackingTab()}
              </Tab>
            </Tabs>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide}>
            Close
          </Button>
          {(activeTab === "documents" || activeTab === "components") && (
            <Button
              variant="outline-primary"
              onClick={() => {
                setShowModal(activeTab);
                console.log("Edit vehicle:", vehicle.vehicle_id);
              }}
            >
              <FaEdit className="me-2" />
              {activeTab === "documents" && "Add Document"}
              {activeTab === "components" && "Add Component"}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
      {showModal === "documents" && (
        <DocumentUploadModal
          entity="vehicle"
          entity_id={vehicle.vehicle_id}
          showModal={showModal === "documents"}
          setShowModal={setShowModal}
          onDocumentAdded={refetch}
        />
      )}
    </>
  );
}

export default VehicleDetailsModal;
